// Single catalogue of the matching health metrics. Both surfaces read it:
// the KPI cards on the dashboard and the "Все метрики" modal. Keeping the
// highlight thresholds here is the whole point — a card and its row in the
// modal cannot drift apart, because neither owns a threshold of its own.
//
// These thresholds colour the KPI numbers only. They are NOT the alert engine:
// alerts arrive from the matching service with their severity already decided
// (see matching docs/dashboard.md), and the dashboard renders them as they come.
import { Kpis } from './types'
import { money, nf } from './consts'
import { formatDurationSec, PAYOUT_TIMEOUT_SEC } from './duration'

/**
 * Доля когорты в процентах. Пустой знаменатель — `null`, а не 0: «ни одной заявки
 * не вошло» и «ни одной не выдали реквизит» — разные утверждения.
 */
export const cohortPct = (part: number, total: number): number | null =>
  total > 0 ? (part / total) * 100 : null

/**
 * Сравнение с предыдущим окном. `null`, если сравнивать не с чем или изменение
 * меньше `eps` — «не изменилось» рисуется отсутствием стрелки, а не нулём.
 */
const trendOf = (current: number | null, previous: number | null, eps: number): Trend | null => {
  if (current === null || previous === null) return null
  const delta = current - previous
  if (Math.abs(delta) < eps) return null
  return { delta, direction: delta > 0 ? 'up' : 'down' }
}

// Bootstrap contextual colour, also used as the state key.
export type MetricState = 'danger' | 'warning' | 'success' | 'info' | 'primary'

// The rule that turns a raw number into a highlight, plus how to say it out loud.
type Highlight = {
  state: Extract<MetricState, 'danger' | 'warning'>
  hit: (value: number) => boolean
  label: string
}

/** Строка под числом карточки; `indent` — подчинённая строка «в том числе …». */
export type SubLine = { text: string; indent?: boolean }

/**
 * Сравнение с предыдущим окном такой же длины. `delta` — разница в единицах
 * самого показателя, `direction` — куда он ушёл. Вердикта «хорошо/плохо» тренд не
 * несёт: у показателя без нормы направление — это факт, а не оценка.
 */
export type Trend = { delta: number; direction: 'up' | 'down' }

/**
 * Режим карточки: один и тот же показатель по разной выборке. `short` — подпись на
 * переключателе (карточка узкая), `label` — полное название в подсказке.
 */
export type MetricMode = { key: string; label: string; short: string }

export type Metric = {
  key: string
  title: string
  /** Key into METRIC_HINTS — the "(?)" explanation shown next to the title. */
  hintKey: string
  /** What exactly is being compared, in the snapshot's own terms. */
  analyzed: string
  /**
   * Переключатель режимов на карточке; первый — режим по умолчанию. Выбранный
   * режим приходит в `value`/`sub`/`subLines`/`trend`, модалка «Все метрики» его не
   * передаёт и показывает первый.
   */
  modes?: MetricMode[]
  /** Headline number, already formatted. */
  value: (kpis: Kpis, mode?: string) => string
  /** Secondary line under the number. */
  sub?: (kpis: Kpis, mode?: string) => string
  /**
   * Несколько строк под числом — для карточек, где одной мало. Читает только
   * карточка; модалка «Все метрики» остаётся на `sub`, поэтому метрика с
   * `subLines` обязана задать и его.
   *
   * `indent` сдвигает строку вправо: подчинённая строка («в том числе …») должна
   * читаться как часть предыдущей, а не как ещё один самостоятельный показатель.
   */
  subLines?: (kpis: Kpis, mode?: string) => SubLine[]
  /**
   * Стрелка рядом с числом: куда показатель ушёл против предыдущего окна.
   * `null` — сравнивать не с чем (за прошлое окно нет данных) либо изменения нет.
   */
  trend?: (kpis: Kpis, mode?: string) => Trend | null
  /**
   * The number the highlight rule judges. Absent → informational metric.
   * `null` → нет данных за окно: метрика уходит в `neutral`, а не в success —
   * пустая выборка не «в норме», красить в ней нечего.
   */
  measure?: (kpis: Kpis) => number | null
  /** Одно правило или лестница правил, проверяемая worst-first. */
  highlight?: Highlight | Highlight[]
  /** Colour for metrics without a rule — they report, they don't warn. Он же
   *  цвет метрики с правилами, у которой за окно нет данных. */
  neutral?: Extract<MetricState, 'info' | 'primary'>
  /** Drill-down signal, when the metric has rows behind it. */
  signal?: string
}

/** Сохранённый режим карточки; незнакомое или пустое значение — первый режим. */
export const readMetricMode = (metric: Metric, raw: string | null): string | undefined =>
  metric.modes?.some((x) => x.key === raw) ? (raw as string) : metric.modes?.[0].key

const PAYOUT_NEED_REAL = 'real'

// Депозиты и коэффициент выбранного режима карточки потребности. Во втором режиме
// поля может не быть (мэтчинг на стенде ещё без него) — тогда null, а не ноль.
const payoutNeedView = (k: Kpis, mode?: string) => {
  const n = k.payout_need_24h
  if (mode !== PAYOUT_NEED_REAL) {
    return { deposits: n.deposits, ratio: n.ratio, prevRatio: n.prev.ratio }
  }
  return {
    deposits: n.real_demand?.deposits ?? null,
    ratio: n.real_demand?.ratio ?? null,
    prevRatio: n.prev.real_demand?.ratio ?? null,
  }
}

// Среднее и медиана времени завершения выплаты отличаются только тем, какое поле
// снапшота читают: выборка, формат, пороги и «нет данных» у них общие. Отдельные
// записи каталога, а не два значения в одной карточке, — чтобы у каждой был свой
// заголовок, свой «(?)» и своя строка в модалке, и чтобы лишнюю можно было убрать
// удалением одной строки.
function completionTimeMetric(
  key: string,
  title: string,
  pick: (kpis: Kpis) => number | null | undefined
): Metric {
  return {
    key,
    title,
    hintKey: key,
    analyzed: `${key}_24h_sec (COMPLETED за 24ч)`,
    // `?? null` — не косметика: пока в среду не уехал мэтчинг с новым полем,
    // снапшот приходит вообще без него, и без нормализации карточка покажет
    // зелёное «NaN мин».
    value: (k) => {
      const sec = pick(k) ?? null
      return sec === null ? 'нет данных' : formatDurationSec(sec)
    },
    sub: (k) => `${nf.format(k.avg_completion_sample_24h ?? 0)} закрытий за 24ч`,
    measure: (k) => pick(k) ?? null,
    // Пороги — доля от таймаута выплаты, а не абсолютные минуты: сместится
    // таймаут матчинга — сместятся и они.
    highlight: [
      {
        state: 'danger',
        hit: (v) => v >= 0.8 * PAYOUT_TIMEOUT_SEC,
        label: '≥ 80% таймаута (3 ч 12 мин)',
      },
      {
        state: 'warning',
        hit: (v) => v >= 0.5 * PAYOUT_TIMEOUT_SEC,
        label: '≥ 50% таймаута (2 ч)',
      },
    ],
    neutral: 'info',
  }
}

export const METRICS: Metric[] = [
  {
    key: 'active_payouts',
    title: 'Активные выплаты',
    hintKey: 'active_payouts',
    analyzed: 'active_payouts.count',
    value: (k) => nf.format(k.active_payouts.count),
    sub: (k) => `остаток ${nf.format(k.active_payouts.remainder_amount)} ${k.currency}`,
    neutral: 'primary',
    signal: 'active_payouts',
  },
  {
    key: 'active_deposits',
    title: 'Активные депозиты',
    hintKey: 'active_deposits',
    analyzed: 'active_deposits.count',
    value: (k) => nf.format(k.active_deposits.count),
    sub: (k) => `${nf.format(k.active_deposits.amount)} ${k.currency}`,
    neutral: 'info',
    signal: 'active_deposits',
  },
  // Заменили «Загрузку ёмкости» (CPAYMENT-1688): один процент по худшей корзине
  // операторам мало что давал, разбивка целиком есть в BasketsChart и BASKET_FULL.
  // Среднее и медиана выведены парой временно — обе читают одну выборку, позже
  // останется одна; убирая, удаляй запись целиком, а не правь вторую.
  completionTimeMetric(
    'avg_completion_time',
    // Без «за» — как у соседних «Отказы flow-control 1ч» / «Антифрод (красные) 24ч».
    'Среднее время завершения выплаты 24ч',
    (k) => k.avg_completion_time_24h_sec
  ),
  completionTimeMetric(
    'median_completion_time',
    'Медианное время завершения выплаты 24ч',
    (k) => k.median_completion_time_24h_sec
  ),
  {
    key: 'effective_capacity',
    title: 'Принимаемость спроса',
    hintKey: 'effective_capacity',
    analyzed: 'effective_capacity.coverable_pct',
    value: (k) => `${k.effective_capacity.coverable_pct.toFixed(0)}%`,
    sub: (k) =>
      `${nf.format(k.effective_capacity.servable)} из ${nf.format(
        k.effective_capacity.total
      )} депозитов`,
    measure: (k) => k.effective_capacity.coverable_pct,
    highlight: { state: 'warning', hit: (v) => v < 70, label: '< 70%' },
    signal: 'active_deposits',
  },
  {
    key: 'conversion',
    title: 'Конверсия закрытия выплат',
    hintKey: 'conversion',
    analyzed: 'conversion_24h_pct',
    value: (k) => `${k.conversion_24h_pct.toFixed(0)}%`,
    sub: () => 'за 24ч',
    measure: (k) => k.conversion_24h_pct,
    highlight: { state: 'warning', hit: (v) => v < 60, label: '< 60%' },
  },
  {
    key: 'flowcontrol_rejects',
    title: 'Отказы flow-control 1ч',
    hintKey: 'flowcontrol_rejects',
    analyzed: 'flowcontrol_reject_1h_pct',
    value: (k) => `${k.flowcontrol_reject_1h_pct.toFixed(0)}%`,
    measure: (k) => k.flowcontrol_reject_1h_pct,
    highlight: { state: 'warning', hit: (v) => v >= 25, label: '≥ 25%' },
    signal: 'flowcontrol_rejects',
  },
  {
    key: 'overdue_payouts',
    title: 'Просрочено выплат',
    hintKey: 'overdue_payouts',
    analyzed: 'overdue_payouts_count',
    value: (k) => nf.format(k.overdue_payouts_count),
    sub: () => '> таймаута',
    measure: (k) => k.overdue_payouts_count,
    highlight: { state: 'danger', hit: (v) => v > 0, label: '> 0' },
    signal: 'payouts_overdue',
  },
  {
    key: 'antifraud',
    title: 'Антифрод (красные) 24ч',
    hintKey: 'antifraud',
    analyzed: 'antifraud.red_count / total',
    value: (k) => `${k.antifraud.red_pct.toFixed(0)}%`,
    sub: (k) => `${nf.format(k.antifraud.red_count)} из ${nf.format(k.antifraud.total)}`,
    neutral: 'info',
    signal: 'antifraud_red',
  },
  {
    key: 'requisite_rate',
    title: 'Процент выдачи реквизита',
    hintKey: 'requisite_rate',
    analyzed: 'payout_need_24h.deposits_matched / deposits (24ч, по штукам)',
    // Первый переход воронки мэтчинга: вошло в мэтчинг → реквизит подобран.
    // В аналитическом слое тот же показатель зовётся requisite_obtained_rate_pct.
    value: (k) => {
      const pct = cohortPct(
        k.payout_need_24h.deposits_matched.count,
        k.payout_need_24h.deposits.count
      )
      return pct === null ? 'нет данных' : `${pct.toFixed(2)}%`
    },
    sub: (k) =>
      `${nf.format(k.payout_need_24h.deposits_matched.count)} из ${nf.format(
        k.payout_need_24h.deposits.count
      )} заявок за 24ч`,
    trend: (k) =>
      trendOf(
        cohortPct(k.payout_need_24h.deposits_matched.count, k.payout_need_24h.deposits.count),
        cohortPct(
          k.payout_need_24h.prev.deposits_matched.count,
          k.payout_need_24h.prev.deposits.count
        ),
        0.005
      ),
    neutral: 'info',
    signal: 'requisite_rate',
  },
  {
    key: 'payout_need',
    title: 'Потребность выплат к депозитам',
    hintKey: 'payout_need',
    analyzed: 'payout_need_24h (24ч)',
    modes: [
      { key: 'all', label: 'Всё в матчинг', short: 'Всё' },
      { key: PAYOUT_NEED_REAL, label: 'Реальный спрос', short: 'Реальный' },
    ],
    // Нормы у показателя нет — он сообщает, а не судит, поэтому ни measure,
    // ни highlight: карточка всегда нейтральная.
    value: (k, mode) => {
      const { ratio } = payoutNeedView(k, mode)
      return ratio === null ? 'нет данных' : ratio.toFixed(2)
    },
    // Ничего не закрылось за одно из окон — сравнивать не с чем; равенство до
    // сотых тоже не тренд, стрелку в этом случае не рисуем. У каждого режима
    // тренд по своей выборке.
    trend: (k, mode) => {
      const { ratio, prevRatio } = payoutNeedView(k, mode)
      return trendOf(ratio, prevRatio, 0.005)
    },
    sub: (k) =>
      `депозиты ${money(k.payout_need_24h.deposits.amount)}, выплаты ${money(
        k.payout_need_24h.payouts.amount
      )}, выплачено ${money(k.payout_need_24h.payouts_paid.amount)}`,
    subLines: (k, mode) => {
      const { deposits } = payoutNeedView(k, mode)
      return [
        {
          text:
            deposits === null
              ? 'Депозиты нет данных'
              : `Депозиты ${money(deposits.amount)} (${nf.format(deposits.count)})`,
        },
        {
          text: `Выплаты ${money(k.payout_need_24h.payouts.amount)} (${nf.format(
            k.payout_need_24h.payouts.count
          )})`,
        },
        {
          text: `в т. ч. выплачено ${money(k.payout_need_24h.payouts_paid.amount)} (${nf.format(
            k.payout_need_24h.payouts_paid.count
          )})`,
          indent: true,
        },
      ]
    },
    neutral: 'info',
  },
]

const rulesOf = (metric: Metric): Highlight[] =>
  metric.highlight === undefined
    ? []
    : Array.isArray(metric.highlight)
    ? metric.highlight
    : [metric.highlight]

/** Current highlight state of a metric: the rule fires, or the metric is fine. */
export function metricState(metric: Metric, kpis: Kpis): MetricState {
  const neutral = metric.neutral ?? 'info'
  if (!metric.highlight || !metric.measure) return neutral
  const value = metric.measure(kpis)
  // Нет данных ≠ «в норме»: зелёный на пустой выборке врёт.
  if (value === null) return neutral
  // Правила упорядочены worst-first — первое сработавшее и есть состояние.
  return rulesOf(metric).find((r) => r.hit(value))?.state ?? 'success'
}

/** Threshold as staff read it; informational metrics have none. */
export function metricThreshold(metric: Metric): string {
  const rules = rulesOf(metric)
  return rules.length > 0 ? rules.map((r) => r.label).join(' / ') : '—'
}

// Grouping band of the modal. `primary` and `info` are two shades of the same
// "reports, doesn't warn" idea, so they share one band — otherwise the modal
// would show two adjacent groups under the same heading.
export type MetricBand = 'danger' | 'warning' | 'success' | 'info'

// Worst first: the modal is read top-down when something is on fire.
export const BAND_ORDER: MetricBand[] = ['danger', 'warning', 'success', 'info']

export const BAND_TITLE: Record<MetricBand, string> = {
  danger: 'Критично',
  warning: 'Внимание',
  success: 'В норме',
  info: 'Информационно',
}

export function metricBand(state: MetricState): MetricBand {
  return state === 'primary' ? 'info' : state
}
